import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteUserById,
  fetchAllUsers,
  toggleUserStatus,
  deleteUserAlls,
  toggleUserVerificationStatus,
} from "../../redux/user/userThunks";
import { setFilters } from "../../redux/user/userSlice";
import { Modal, Button } from "react-bootstrap";

const UserListLayer = ({ roles }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const authToken = localStorage.getItem("authToken");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectreson, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [localSort, setLocalSort] = useState({
    sortColumn: "",
    sortOrder: "asc",
  });
  const [ageSorting, setAgeSorting] = useState(false);
  const [ageSortedAll, setAgeSortedAll] = useState(null); // full sorted array for age sort
  const [ageLocalPage, setAgeLocalPage] = useState(1); // current page during age sort
  // Inside the component:
  const dispatch = useDispatch();
  const {
    userList: items,
    totalPages,
    totalUsers,
    loading,
    error,
    filters: {
      currentPage,
      itemsPerPage,
      searchQuery,
      sortColumn,
      sortOrder,
      statusFilter,
      roleFilter,
      startDate,
      endDate,
      VerifiedstatusFilter,
    },
  } = useSelector((state) => state.user);

  const calculateAge = (dob) => {
    if (!dob) return null;

    // Parse as local calendar date to avoid timezone shifting DOB back a day
    const dobStr = String(dob).split("T")[0];
    const [year, month, day] = dobStr.split("-").map(Number);
    if (!year || !month || !day) return null;

    const today = new Date();
    let age = today.getFullYear() - year;
    const monthDiff = today.getMonth() + 1 - month;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
      age--;
    }
    return age >= 0 ? age : null;
  };

  const isAgeSortActive = localSort.sortColumn === "age";

  const sortUsersByAge = (list, order) => {
    return [...list].sort((a, b) => {
      const ageA = calculateAge(a.dateOfBirth);
      const ageB = calculateAge(b.dateOfBirth);
      const aIsNA = ageA == null || Number.isNaN(ageA);
      const bIsNA = ageB == null || Number.isNaN(ageB);

      // ASC: digits first, then all N/A at the end
      // DESC: all N/A first, then digits
      if (aIsNA && bIsNA) return 0;
      if (aIsNA) return order === "asc" ? 1 : -1;
      if (bIsNA) return order === "asc" ? -1 : 1;

      return order === "asc" ? ageA - ageB : ageB - ageA;
    });
  };

  const paginateAgeList = (list, page, limit) => {
    const start = (page - 1) * limit;
    return list.slice(start, start + limit);
  };

  // Replace your fetchData with:
  const fetchData = (page) => {
    // While age-sorting, list comes from frontend cache (ageSortedAll)
    if (isAgeSortActive) {
      if (ageSortedAll) {
        setTableData(paginateAgeList(ageSortedAll, page, itemsPerPage));
      }
      return;
    }

    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";
    //  Validation: check if start date is greater than end date
    if (
      formattedStartDate &&
      formattedEndDate &&
      moment(formattedStartDate).isAfter(moment(formattedEndDate))
    ) {
      setErrorMessage("Start date cannot be greater than end date");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return; // stop fetch
    }

    const isAgeSearch = searchQuery && !isNaN(searchQuery.trim());

    const params = {
      page: isAgeSearch ? 1 : page,
      limit: isAgeSearch ? 99999 : itemsPerPage,
      search: isAgeSearch ? "" : searchQuery, // skip backend search for age number
      sortBy: sortColumn,
      sortOrder: sortOrder,
      status: statusFilter,
      role: roles,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    dispatch(fetchAllUsers({ params, navigate }));
  };

  useEffect(() => {
    // ✅ Clear only search on full page reload
    dispatch(setFilters({ searchQuery: "" }));
  }, [dispatch]);

  useEffect(() => {
    fetchData(currentPage);
  }, [
    currentPage,
    searchQuery,
    itemsPerPage,
    sortOrder,
    sortColumn,
    statusFilter,
    roleFilter,
    startDate,
    endDate,
    VerifiedstatusFilter,
    isAgeSortActive,
    ageSortedAll,
  ]);

  // Exit frontend age-sort when filters/search change so list refreshes from backend
  useEffect(() => {
    if (!isAgeSortActive) return;
    setLocalSort({ sortColumn: "", sortOrder: "asc" });
    setAgeSortedAll(null);
  }, [
    searchQuery,
    statusFilter,
    roleFilter,
    startDate,
    endDate,
    VerifiedstatusFilter,
  ]);

  useEffect(() => {
    // When age sort is active, table is already managed by ageSortedAll pagination
    if (isAgeSortActive) return;

    let data = [...items];

    // If search query is a number, filter locally by exact age match
    if (searchQuery && !isNaN(searchQuery.trim())) {
      const searchAge = parseInt(searchQuery.trim(), 10);
      data = data.filter(
        (user) => calculateAge(user.dateOfBirth) === searchAge,
      );
    }

    setTableData(data);
  }, [items, searchQuery, isAgeSortActive]);

  const isSelected = (id) => selectedItems.includes(id);
  const handleDeleteAll = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : selectedItems;
    if (idsToDelete.length === 0) {
      return;
    }
    const message = `This action will delete the User!`;
    const confirmmessage = `Yes, delete it!`;
    const result = await showConfirmationDialog(message, confirmmessage);

    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(
        deleteUserAlls({ id: idsToDelete, navigate }),
      );
      if (data.payload.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      if (data.payload.status != 200) {
        throw new Error(`HTTP error! status: ${data.payload.status}`);
      }
      const successmessage =
        single === "Yes"
          ? "User deleted successfully"
          : "Users deleted successfully";
      setSuccessMessage(successmessage);
      setTimeout(() => {
        setSuccessMessage("");
        //window.location.reload();
      }, 3000);

      if (single === "No") {
        setSelectedItems([]);
      }
      // Clear age-sort cache so list refreshes correctly
      setAgeSortedAll(null);
      setLocalSort({ sortColumn: "", sortOrder: "asc" });
      fetchData(currentPage);
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      //  }
    }
  };

  const handlePageChange = (pageNumber) => {
    const pages = isAgeSortActive
      ? Math.max(1, Math.ceil((ageSortedAll?.length || 0) / itemsPerPage))
      : totalPages;
    if (pageNumber >= 1 && pageNumber <= pages) {
      dispatch(setFilters({ currentPage: pageNumber }));
    }
  };

  const handleSort = async (column) => {
    // Age sort: frontend only (asc/desc toggle like other columns)
    if (column === "age") {
      const newOrder =
        localSort.sortColumn === "age" && localSort.sortOrder === "asc"
          ? "desc"
          : "asc";

      setLocalSort({ sortColumn: "age", sortOrder: newOrder });
      setAgeSorting(true);
      dispatch(setFilters({ currentPage: 1 }));

      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      try {
        const action = await dispatch(
          fetchAllUsers({
            params: {
              page: 1,
              limit: 99999,
              search:
                searchQuery && !isNaN(searchQuery.trim())
                  ? ""
                  : searchQuery || "",
              sortBy: "createdAt",
              sortOrder: "desc",
              status: statusFilter,
              role: roles,
              startDate: formattedStartDate,
              endDate: formattedEndDate,
            },
            navigate,
          }),
        );

        let allUsers = action.payload?.users || [];

        // Keep numeric age search working on frontend
        if (searchQuery && !isNaN(searchQuery.trim())) {
          const searchAge = parseInt(searchQuery.trim(), 10);
          allUsers = allUsers.filter(
            (user) => calculateAge(user.dateOfBirth) === searchAge,
          );
        }

        const sorted = sortUsersByAge(allUsers, newOrder);
        setAgeSortedAll(sorted);
        setTableData(paginateAgeList(sorted, 1, itemsPerPage));
      } catch (err) {
        setErrorMessage("Failed to sort by age");
        setTimeout(() => setErrorMessage(""), 3000);
      } finally {
        setAgeSorting(false);
      }
      return;
    }

    // Other columns: backend sorting
    setLocalSort({ sortColumn: "", sortOrder: "asc" });
    setAgeSortedAll(null);

    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    dispatch(
      setFilters({
        sortColumn: column,
        sortOrder: newOrder,
        currentPage: 1,
      }),
    );
  };

  const handleItemsPerPageChange = (e) => {
    dispatch(
      setFilters({
        itemsPerPage: parseInt(e.target.value, 10),
        currentPage: 1, // reset to first page
      }),
    );
  };

  const effectiveTotalUsers = isAgeSortActive
    ? ageSortedAll?.length || 0
    : totalUsers;
  const effectiveTotalPages = isAgeSortActive
    ? Math.max(1, Math.ceil(effectiveTotalUsers / itemsPerPage))
    : totalPages;

  const handleNext = () => {
    if (currentPage < effectiveTotalPages) {
      dispatch(setFilters({ currentPage: currentPage + 1 }));
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      dispatch(setFilters({ currentPage: currentPage - 1 }));
    }
  };

  const updateStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const message = `Do you want to change the status to ${newStatus}?`;

    const result = await showConfirmationDialog(message);
    if (!result.isConfirmed) return;

    try {
      await dispatch(
        toggleUserStatus({ id, data: { status: newStatus }, navigate }),
      ).unwrap();
      setSuccessMessage(`User status updated to ${newStatus}`);
    } catch (err) {
      const errorMsg = err?.message || "Failed to update user status";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const updateVerificationStatus = async (id, verificationStatus) => {
    if (verificationStatus === "reject") {
      setSelectedUserId(id);
      setRejectReason("");
      setRejectReasonError("");
      setShowRejectModal(true);
      return;
    }

    const result = await showConfirmationDialog(
      "Do you want to approve this promoter?",
    );

    if (!result.isConfirmed) return;

    try {
      await dispatch(
        toggleUserVerificationStatus({
          id,
          data: {
            verifiedstatus: "approve",
          },
          navigate,
        }),
      ).unwrap();

      setSuccessMessage("Promoter approved successfully.");
      fetchData(currentPage); // Reload the data list
    } catch (err) {
      setErrorMessage(err?.message || "Failed to update status.");
    }
  };

  const handleRejectReasonChange = (e) => {
    let value = e.target.value;

    // Remove special characters (only allow letters, digits, and spaces)
    value = value.replace(/[^a-zA-Z0-9 ]/g, "");

    // Prevent double spaces - replace multiple spaces with single space
    value = value.replace(/  +/g, " ");

    setRejectReason(value);
  };

  const handleReject = async () => {
    if (!rejectreson.trim()) {
      setRejectReasonError("Reason is required.");
      return;
    }

    try {
      await dispatch(
        toggleUserVerificationStatus({
          id: selectedUserId,
          data: {
            verifiedstatus: "reject",
            rejectreson,
          },
          navigate,
        }),
      ).unwrap();

      setSuccessMessage("Promoter rejected successfully.");
      setShowRejectModal(false);
      setRejectReason("");
      setRejectReasonError("");
      fetchData(currentPage); // Reload the data list
    } catch (err) {
      setErrorMessage(err?.message || "Failed to reject promoter.");
    }
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(effectiveTotalPages, start + 4); // Ensure only 5 items are shown (start + 4)

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const handleDeleteUser = async (id) => {
    const result = await showConfirmationDialog(
      "Are you sure you want to delete this user?",
    );
    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(deleteUserById({ id, navigate })).unwrap();
      setSuccessMessage(data?.message || "User deleted successfully");

      // 🔥 Refresh list after delete
      const newTotalUsers = totalUsers - 1;
      const lastPage = Math.ceil(newTotalUsers / itemsPerPage);

      // if deleting last user on current page, move to previous page
      if (items.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1);
        dispatch(setFilters({ currentPage: currentPage - 1 }));
      } else {
        fetchData(currentPage);
      }
    } catch (err) {
      const errorMsg =
        typeof err === "string" ? err : err?.message || "Failed to delete user";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((item) => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((item) => item !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const path =
    roles === "host"
      ? "host/view-host"
      : roles === "promoter"
        ? "promoter/view-promoter"
        : "user/view-user";
  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            defaultValue="Select Number"
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>
          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                dispatch(
                  setFilters({
                    searchQuery: e.target.value,
                  }),
                );
              }}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  dispatch(setFilters({ currentPage: 1 }));
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />

            {/* Search Icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear (Close) Icon */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      searchQuery: "",
                      currentPage: 1,
                    }),
                  );
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) =>
              dispatch(
                setFilters({
                  statusFilter: e.target.value,
                  currentPage: 1, // reset to page 1 on filter change
                }),
              )
            }
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {roles == "promoter" && (
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={VerifiedstatusFilter}
              onChange={(e) =>
                dispatch(
                  setFilters({
                    VerifiedstatusFilter: e.target.value,
                    currentPage: 1, // reset to page 1 on filter change
                  }),
                )
              }
            >
              <option value="">Verification Status</option>
              <option value="pending">Pending</option>
              <option value="approve">Approve</option>
              <option value="reject">Reject</option>
            </select>
          )}

          {/* <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={roleFilter}
            onChange={(e) =>
              dispatch(
                setFilters({
                  roleFilter: e.target.value,
                  currentPage: 1, // reset to page 1 on filter change
                })
              )
            }
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="host">Host</option>
          </select> */}
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                dispatch(
                  setFilters({
                    startDate: date,
                    currentPage: 1,
                  }),
                );
              }}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />

            {startDate && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      startDate: null,
                      currentPage: 1,
                    }),
                  );
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                dispatch(
                  setFilters({
                    endDate: date,
                    currentPage: 1,
                  }),
                );
              }}
              minDate={startDate} // Disable dates before the start date
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
            />

            {endDate && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      endDate: null,
                      currentPage: 1,
                    }),
                  );
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>
          <button
            className="btn btn-danger"
            onClick={handleDeleteAll}
            disabled={selectedItems.length === 0}
          >
            Delete All
          </button>
        </div>
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>
                  <div className="form-check style-check d-flex align-items-center">
                    <input
                      className="form-check-input radius-4 border input-form-dark"
                      type="checkbox"
                      id="selectAll"
                      onChange={handleSelectAll}
                      checked={
                        selectedItems.length === items.length &&
                        items.length > 0
                      }
                    />
                  </div>
                </th>
                {/* <th>
                  <div className="d-flex align-items-center gap-0">Role</div>
                </th> */}
                <th
                  onClick={() => handleSort("fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          localSort.sortColumn !== "age" &&
                          sortColumn === "fullName" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          localSort.sortColumn !== "age" &&
                          sortColumn === "fullName" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>

                <th
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Email
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          localSort.sortColumn !== "age" &&
                          sortColumn === "email" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          localSort.sortColumn !== "age" &&
                          sortColumn === "email" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("age")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Age
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          localSort.sortColumn === "age" &&
                          localSort.sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          localSort.sortColumn === "age" &&
                          localSort.sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Signup date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          localSort.sortColumn !== "age" &&
                          sortColumn === "createdAt" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          localSort.sortColumn !== "age" &&
                          sortColumn === "createdAt" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading || ageSorting ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : tableData.length > 0 ? (
                tableData.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border"
                          type="checkbox"
                          value={user._id}
                          checked={isSelected(user._id)}
                          onChange={() => handleSelectItem(user._id)}
                        />
                      </div>
                    </td>
                    {/* <td>
                      <span
                        className="d-inline-block"
                        style={{ maxWidth: "150px" }}
                      >
                        {user?.currentRole || "N/A"}
                      </span>
                    </td> */}
                    <td>
                      <span
                        className="d-inline-block text-truncate"
                        style={{ maxWidth: "150px" }}
                        title={user?.fullName || "N/A"}
                      >
                        {user?.fullName || "N/A"}
                      </span>
                    </td>
                    <td>{user?.email || "N/A"}</td>
                    {/* <td>{user?.age || "N/A"}</td> */}
                    <td>{calculateAge(user.dateOfBirth) ?? "N/A"}</td>

                    <td>{moment(user?.createdAt).format("DD-MM-YYYY")}</td>
                    <td>
                      <span
                        onClick={() => updateStatus(user?._id, user?.status)}
                        className={`${
                          user.status === "active"
                            ? "bg-success-focus text-success-600 border border-success-main"
                            : user.status === "banned"
                              ? "bg-danger-focus text-danger-600 border border-danger-main"
                              : "bg-neutral-200 text-neutral-600 border border-neutral-400"
                        } px-24 py-4 radius-4 fw-medium text-sm`}
                        style={{ cursor: "pointer" }}
                      >
                        {user.status.charAt(0).toUpperCase() +
                          user.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <button
                          title="View"
                          onClick={() => navigate(`/${path}/${user._id}`)}
                          className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="majesticons:eye-line" />
                        </button>
                        {user.currentRole === "promoter" && (
                          <>
                            {/* Verification Actions */}

                            {user.verifiedstatus === "pending" && (
                              <>
                                {/* Approve */}
                                <button
                                  title="Approve"
                                  onClick={() =>
                                    updateVerificationStatus(
                                      user._id,
                                      "approve",
                                    )
                                  }
                                  className="bg-success-focus text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                >
                                  <Icon icon="mdi:check" />
                                </button>

                                {/* Reject */}
                                <button
                                  title="Reject"
                                  onClick={() =>
                                    updateVerificationStatus(user._id, "reject")
                                  }
                                  className="bg-danger-focus text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                >
                                  <Icon icon="mdi:close" />
                                </button>
                              </>
                            )}

                            {user.verifiedstatus === "approve" && (
                              <button
                                title="Reject"
                                onClick={() =>
                                  updateVerificationStatus(user._id, "reject")
                                }
                                className="bg-danger-focus text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              >
                                <Icon icon="mdi:close" />
                              </button>
                            )}

                            {user.verifiedstatus === "reject" && (
                              <button
                                title="Approve"
                                onClick={() =>
                                  updateVerificationStatus(user._id, "approve")
                                }
                                className="bg-success-focus text-success-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              >
                                <Icon icon="mdi:check" />
                              </button>
                            )}
                          </>
                        )}

                        <button
                          title="Delete"
                          onClick={() => handleDeleteUser(user._id)}
                          className="bg-danger-focus text-danger-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {effectiveTotalUsers === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, effectiveTotalUsers)} of{" "}
                {effectiveTotalUsers} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {getPaginationRange().map((page) => {
              // Return JSX for pagination items
              return (
                <li key={page} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === page
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200"
                    }`}
                    to="#"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Link>
                </li>
              );
            })}

            <li
              className={`page-item ${
                currentPage === effectiveTotalPages ? "disabled" : ""
              }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <Modal
        show={showRejectModal}
        onHide={() => {
          setShowRejectModal(false);
          setRejectReasonError("");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject Reason</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <label className="form-label">
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            className={`form-control ${rejectReasonError ? "is-invalid" : ""}`}
            rows={4}
            value={rejectreson}
            onChange={handleRejectReasonChange}
            onKeyUp={() => {
              if (rejectReasonError && rejectreson.trim()) {
                setRejectReasonError("");
              }
            }}
            placeholder="Enter rejection reason..."
          />
          {rejectReasonError && (
            <div className="invalid-feedback d-block">{rejectReasonError}</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRejectModal(false);
              setRejectReasonError("");
            }}
          >
            Cancel
          </Button>

          <Button variant="danger" onClick={handleReject}>
            Reject
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserListLayer;
